import clsx from "clsx";
import MergeLinkCardFooter from "./MergeLinkCardFooter";
import { useEffect, useState } from "react";
import useAgentHandlerLinkModalContext from "../context/useAgentHandlerLinkModalContext";

// constants
const ANIMATION_DURATION_MILLISECONDS = 275; // 25ms faster than actual animation so that it renders a bit snappier

export type FooterProps = {
  showFooter?: boolean;
  footerChildren?: React.ReactNode;
  onFooterClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

type MergeLinkCardBaseProps = {
  children: React.ReactNode;
  size?: "sm" | "lg";
} & FooterProps;

const MergeLinkCardBase = ({
  children,
  size = "sm",
  showFooter = false,
  footerChildren,
  onFooterClick,
}: MergeLinkCardBaseProps) => {
  // hooks
  const { modalSize, setModalSize, modalBorderRadius } = useAgentHandlerLinkModalContext();

  // state
  const [showBody, setShowBody] = useState<boolean>();

  // handle resizing component w/ animation
  useEffect(() => {
    // if modalSize at context level is not equal to the passed in value, we need to resize.
    if (modalSize !== size) {
      setTimeout(() => {
        setModalSize(size);
      }, 0); // set to 0 so that it updates state AFTER first painting of DOM (thus triggering css animation)
    }
  }, [modalSize, setModalSize, size]);

  // set initial showBody value
  useEffect(() => {
    // if already set take no action
    if (showBody !== undefined) return;

    if (modalSize === size) {
      setShowBody(true);
    } else {
      // delay showing body until after animation completes
      setShowBody(false);
      setTimeout(() => setShowBody(true), ANIMATION_DURATION_MILLISECONDS);
    }
  }, [modalSize, showBody, size]);

  return (
    <div className="w-full h-full flex flex-col items-center justify-center">
      <div className="relative rounded-xl h-full max-h-[760px] min-h-[440px] transition-all duration-500 delay-200">
        <div
          className={clsx(
            modalSize === "sm" && "w-[375px]",
            modalSize === "lg" && "w-[900px]",
            "relative min-h-[400px] max-h-[720px] h-full rounded-xl transition-all duration-500 delay-200"
          )}
        >
          {/* id is targeted by CustomGlobalStyle for customer border-radius overrides — do not remove */}
          <div
            id="custom-modal-container"
            className={clsx(
              "z-[100] relative overflow-hidden rounded-xl w-full bg-white transition-all h-full"
            )}
            style={{ borderRadius: modalBorderRadius || undefined }}
          >
            {showBody && <div className="flex flex-col h-full relative">{children}</div>}
          </div>
        </div>
        {showFooter && (
          <MergeLinkCardFooter onClick={onFooterClick}>{footerChildren}</MergeLinkCardFooter>
        )}
      </div>
    </div>
  );
};

export default MergeLinkCardBase;
